import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Lock, LogIn, Heart, PoundSterling, Users } from "lucide-react";
import { LOGO_URL } from "@/lib/content";

export default function About() {
  const [ctaHover, setCtaHover] = useState(false);

  return (
    <div
      data-testid="about-page"
      className="min-h-screen bg-black text-[#F5F5F0] grain relative overflow-x-clip"
    >
      {/* HEADER — matches Landing header */}
      <header className="sticky top-0 z-40 bg-black/85 backdrop-blur-md border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 md:px-12 flex items-center justify-between h-20 md:h-24">
          <Link to="/" data-testid="about-logo-link" className="flex items-center">
            <img
              src={LOGO_URL}
              alt="Swingle For Venues"
              className="h-14 md:h-16 w-auto object-contain"
            />
          </Link>
          <nav className="flex items-center gap-2 md:gap-4" aria-label="Primary">
            <Link
              to="/"
              data-testid="about-nav-home"
              className="hidden md:inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.22em] text-[#A3A3A3] hover:text-[#F5F5F0] transition-colors px-3 py-2"
            >
              Home
            </Link>
            <Link
              to="/#contact"
              data-testid="about-nav-contact"
              className="hidden md:inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.22em] text-[#A3A3A3] hover:text-[#F5F5F0] transition-colors px-3 py-2"
            >
              Get Our Venue Info Pack
            </Link>
            <Link
              to="/portal/login"
              data-testid="about-nav-venue-login"
              className="inline-flex items-center gap-1.5 md:gap-2 border border-white/25 hover:border-[#D9A6F5] hover:text-[#D9A6F5] text-[#F5F5F0] px-3.5 md:px-5 py-2.5 text-[10px] md:text-[11px] uppercase tracking-[0.22em] font-bold transition-colors"
            >
              <LogIn size={12} /> Venue Login
            </Link>
            <Link
              to="/#contact"
              data-testid="about-nav-cta"
              className="hidden sm:inline-flex items-center gap-1.5 md:gap-2 bg-[#D9A6F5] text-black hover:bg-[#E8C8FF] px-4 md:px-5 py-2.5 text-[10px] md:text-[11px] uppercase tracking-[0.22em] font-bold transition-colors"
            >
              Get Our Venue Info Pack <ArrowUpRight size={12} />
            </Link>
          </nav>
        </div>
      </header>

      {/* HERO */}
      <section className="relative border-b border-white/10 overflow-hidden">
        <div
          aria-hidden
          className="absolute inset-0 opacity-25 bg-cover bg-center pointer-events-none"
          style={{
            backgroundImage:
              "url('https://images.unsplash.com/photo-1514320291840-2e0a9bf2a9ae?crop=entropy&cs=srgb&fm=jpg&q=85&w=2400')",
          }}
        />
        <div
          aria-hidden
          className="absolute inset-0 pointer-events-none"
          style={{ background: "linear-gradient(180deg, rgba(0,0,0,0.55) 0%, #000 92%)" }}
        />
        <div className="relative max-w-7xl mx-auto px-6 md:px-12 pt-16 md:pt-24 pb-20 md:pb-28">
          <div className="flex items-center gap-3 mb-8">
            <span className="h-px w-10 bg-[#D9A6F5]" />
            <span className="text-[11px] md:text-xs uppercase tracking-[0.3em] text-[#D9A6F5]">
              About Us
            </span>
            <span className="h-px w-10 bg-[#D9A6F5]" />
          </div>
          <h1
            className="font-black tracking-[-0.04em] leading-[0.95] max-w-5xl"
            style={{ fontSize: "clamp(2.75rem, 7.5vw, 6.25rem)" }}
            data-testid="about-hero-title"
          >
            Built by lifestylers,
            <br />
            for the venues we
            <span className="font-accent text-[#D9A6F5] inline-block -rotate-2 ml-3">love.</span>
          </h1>
          <p className="mt-8 text-lg md:text-xl text-[#A3A3A3] leading-relaxed max-w-2xl">
            We&apos;re a small team on a mission — make sure every venue we work with keeps the money
            they earn, and reinvests it where it counts.
          </p>
        </div>
      </section>

      {/* STORY — user's verbatim copy */}
      <section className="border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-32 grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-16">
          <div className="md:col-span-4">
            <div className="text-xs uppercase tracking-[0.3em] text-[#D9A6F5] mb-6">
              Our Story
            </div>
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight leading-tight">
              Fed up with venues losing out.
            </h2>
          </div>
          <div
            className="md:col-span-8 space-y-8 text-lg md:text-xl text-[#A3A3A3] leading-relaxed"
            data-testid="about-story-copy"
          >
            <p className="text-[#F5F5F0] text-2xl md:text-3xl font-medium tracking-tight leading-snug">
              We are a small team of lifestylers fed up with seeing venues be out of pocket and
              paying high fees to sell tickets and losing out on payment processing fees.
            </p>
            <p>
              We want to give each and every one of our venues the maximum amount of money from
              every ticket they sell so that they can reinvest in their venue and provide a better
              experience for their guests.
            </p>
            <p>
              We&apos;re doing our bit to make the lifestyle a better place to be and would love to
              have your venue as part of the family.
            </p>
          </div>
        </div>
      </section>

      {/* PRINCIPLES */}
      <section className="border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-32">
          <div className="flex items-center gap-3 mb-6">
            <span className="h-px w-10 bg-[#D9A6F5]" />
            <span className="text-xs uppercase tracking-[0.3em] text-[#D9A6F5]">
              What We Stand For
            </span>
          </div>
          <h2 className="text-4xl md:text-6xl font-black tracking-tighter mb-16 max-w-3xl leading-[1]">
            Three promises. No small print.
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-white/10">
            {[
              {
                icon: PoundSterling,
                k: "01",
                t: "Every penny back to you",
                b: "Venues keep 100% of ticket revenue plus a 20p bonus per ticket. We absorb the processing fees so you don't have to.",
              },
              {
                icon: Heart,
                k: "02",
                t: "Lifestyle-first, always",
                b: "We're in the rooms. We know the community. Every feature we ship is built with the lifestyle in mind — not tacked-on generic SaaS.",
              },
              {
                icon: Users,
                k: "03",
                t: "You're family, not a customer",
                b: "No lock-ins, no exit fees, no faceless support desk. We work alongside you to help your venue thrive month after month.",
              },
            ].map(({ icon: Icon, k, t, b }) => (
              <div
                key={k}
                data-testid={`about-principle-${k}`}
                className="bg-black p-10 md:p-12 group hover:bg-[#0A0A0A] transition-colors"
              >
                <div className="flex items-start justify-between mb-6">
                  <div className="text-5xl md:text-6xl font-black tracking-tighter text-[#D9A6F5]">
                    {k}
                  </div>
                  <Icon
                    size={24}
                    className="text-[#D9A6F5]/60 group-hover:text-[#D9A6F5] transition-colors"
                  />
                </div>
                <h3 className="text-2xl font-bold tracking-tight mb-4">{t}</h3>
                <p className="text-[#A3A3A3] leading-relaxed">{b}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* BIG NUMBER STRIP */}
      <section className="border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-24 grid grid-cols-2 md:grid-cols-4 gap-8 md:gap-6">
          {[
            { n: "100%", l: "Ticket revenue kept" },
            { n: "£1.75", l: "Extra per ticket sold" },
            { n: "0", l: "Long-term contracts" },
            { n: "UK", l: "Small team, big hearts" },
          ].map((item) => (
            <div key={item.l}>
              <div className="text-4xl md:text-6xl font-black tracking-tighter text-[#F5F5F0]">
                {item.n}
              </div>
              <div className="mt-3 text-[10px] md:text-xs uppercase tracking-[0.28em] text-[#A3A3A3]">
                {item.l}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section>
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-32 flex flex-col md:flex-row items-start md:items-center justify-between gap-10">
          <div>
            <div className="text-xs uppercase tracking-[0.3em] text-[#D9A6F5] mb-4">
              Ready when you are
            </div>
            <h2 className="text-4xl md:text-6xl font-black tracking-tighter max-w-2xl leading-[1]">
              Let&apos;s make your venue{" "}
              <span className="font-accent text-[#D9A6F5] inline-block -rotate-2">
                more money.
              </span>
            </h2>
          </div>
          <Link
            to="/#contact"
            data-testid="about-bottom-cta"
            onMouseEnter={() => setCtaHover(true)}
            onMouseLeave={() => setCtaHover(false)}
            className="inline-flex items-center gap-3 bg-[#D9A6F5] text-black px-8 py-5 text-sm uppercase tracking-[0.22em] font-bold hover:bg-[#E8C8FF] transition-colors whitespace-nowrap"
          >
            Get in touch{" "}
            <ArrowUpRight
              size={18}
              className={`transition-transform ${ctaHover ? "translate-x-1 -translate-y-1" : ""}`}
            />
          </Link>
        </div>
      </section>

      {/* FOOTER — matches Landing footer */}
      <footer className="relative z-[2] border-t border-white/10">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-8 flex flex-col md:flex-row items-center justify-between gap-3 text-xs text-[#A3A3A3] uppercase tracking-[0.18em]">
          <p>© {new Date().getFullYear()} Swingle For Venues. UK-based, lifestyle-first.</p>
          <div className="flex items-center gap-5">
            <Link
              to="/about"
              data-testid="about-footer-about-link"
              className="text-[#A3A3A3]/80 hover:text-[#D9A6F5] transition-colors"
            >
              About
            </Link>
            <Link
              to="/terms"
              className="text-[#A3A3A3]/80 hover:text-[#D9A6F5] transition-colors"
            >
              Terms
            </Link>
            <Link
              to="/privacy"
              className="text-[#A3A3A3]/80 hover:text-[#D9A6F5] transition-colors"
            >
              Privacy
            </Link>
            <Link
              to="/admin/login"
              className="inline-flex items-center gap-1.5 text-[#A3A3A3]/70 hover:text-[#D9A6F5] transition-colors"
            >
              <Lock size={12} /> Admin
            </Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
